import { useCallback, useEffect, useRef } from 'react';

import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { UiProvider } from 'ui';

/**
 * Custom hook to render a React node to a virtual DOM
 * @returns
 *  - renderToTheVirtualDom: function to render content to a target document
 * - cleanUp: function to clean up the rendered content
 *
 */
const useRenderer = () => {
  /** Ref function to clean up the most recently created frame */
  const cleanUpRef = useRef<(() => void) | null>(null);

  /**
   * Renders a react node to a target document on a new root application and provides that content back along with the root application
   * When no target document is provided, it defaults to the current document
   */
  const renderToTheVirtualDom = useCallback(
    (content: React.ReactNode, targetDocument = document) => {
      const rootDiv = targetDocument?.createElement('div');
      const root = createRoot(rootDiv);

      flushSync(() => {
        root.render(<UiProvider>{content}</UiProvider>);
      });
      const cleanUp = () => {
        root.unmount();
      };
      cleanUpRef.current = cleanUp;
      return { rootDiv, root };
    },
    []
  );

  /** Provides manual clean up of the renderer */
  const cleanUp = useCallback(() => {
    cleanUpRef.current?.();
  }, []);

  // Cleanup the iFrame
  useEffect(() => {
    return () => {
      cleanUp();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Do not want to run this on every render, only on unmount
  }, []);
  return { renderToTheVirtualDom, cleanUp };
};

export { useRenderer };
